import { useAppSelector } from '@/store/hooks';
import { useOpsSettings, useUpdateOpsSettings } from '@/services/queries';
import { canRelease } from '@/types';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { SettingsGroup, SettingsRow } from './SettingsGroup';

/**
 * The guardrails every rollout runs under: the auto-halt and the second
 * approval. Stored in ops_settings; only superadmin and engineering can change
 * them, and the server records who did.
 */
export function RolloutSafety() {
  const settings = useOpsSettings();
  const update = useUpdateOpsSettings();
  const mayChange = canRelease(useAppSelector((state) => state.auth.user?.role));
  const busy = !mayChange || update.isPending || !settings.data;

  const toggle = (checked: boolean, field: 'autoHaltEnabled' | 'requireSecondApproval') => (
    <Switch
      checked={checked}
      disabled={busy}
      onCheckedChange={(next) => {
        update.mutate({ [field]: next });
      }}
      aria-label={
        field === 'autoHaltEnabled' ? 'Auto-halt on crash spike' : 'Require a second approval'
      }
    />
  );

  return (
    <SettingsGroup title="Rollout safety" sub="Guardrails applied to every deployment">
      <SettingsRow
        label="Auto-halt on crash spike"
        desc="Stops offering a build once its crash reports pass 0.8% of its check-ins (judged from 50 check-ins). Machines get the previous build; a new rollout of the halted one resumes it."
        control={
          settings.data ? (
            toggle(settings.data.autoHaltEnabled, 'autoHaltEnabled')
          ) : (
            <Skeleton className="h-5 w-8" />
          )
        }
      />
      <SettingsRow
        label="Require a second approval"
        desc="A stable-channel rollout to production waits at its last stage until a second staff member approves it. Rollbacks, beta builds and staging never wait."
        control={
          settings.data ? (
            toggle(settings.data.requireSecondApproval, 'requireSecondApproval')
          ) : (
            <Skeleton className="h-5 w-8" />
          )
        }
      />
      {!mayChange && settings.data && (
        <p className="px-7 pb-4 text-[12px] text-muted-foreground">
          Changing these needs superadmin or engineering.
        </p>
      )}
    </SettingsGroup>
  );
}
